import { DashboardContext } from "@database/contexts";
import { dashboardService } from "@database/services";
import { useCallback, useContext, useState } from "react";
import { useOrganisation } from "./useOrganisation";
import { ErrorType } from "@utilities/types";

export const useDashboard = () => {
    
    const {
        yearSalesByMonth, setYearSalesByMonth,
        monthSalesByWeek, setMonthSalesByWeek,
        weekSalesByDay, setWeekSalesByDay,
        todaySalesByHour, setTodaySalesByHour,
        topProductsStats, setTopProductsStats,
        averageSalesByHour, setAverageSalesByHour,
        averageSalesByDayOfWeek, setAverageSalesByDayOfWeek,
        sellerPerformance, setSellerPerformance,
        organisationMontlyRevenue, setOrganisationMontlyRevenue,
    } = useContext(DashboardContext);
    const { organisation } = useOrganisation();

    const [getYearSalesByMonthError, setGetYearSalesByMonthError] = useState<ErrorType | null>(null);
    const [getMonthSalesByWeekError, setGetMonthSalesByWeekError] = useState<ErrorType | null>(null);
    const [getWeekSalesByDayError, setGetWeekSalesByDayError] = useState<ErrorType | null>(null);
    const [getTodaySalesByHourError, setGetTodaySalesByHourError] = useState<ErrorType | null>(null);
    const [getTopProductsStatsError, setGetTopProductsStatsError] = useState<ErrorType | null>(null);
    const [getAverageSalesByHourError, setGetAverageSalesByHourError] = useState<ErrorType | null>(null);
    const [getAverageSalesByDayOfWeekError, setGetAverageSalesByDayOfWeekError] = useState<ErrorType | null>(null);
    const [getSellerPerformanceError, setGetSellerPerformanceError] = useState<ErrorType | null>(null);
    const [getOrganisationMontlyRevenueError, setGetOrganisationMontlyRevenueError] = useState<ErrorType | null>(null);

    const fetchAllDashboardData = useCallback(async()=> {
        if (!organisation) return;
    
        const organisationId = organisation._id;

        const [
            resultYearSalesByMonth, resultMonthSalesByWeek,
            resultWeekSalesByDay, resultTodaySalesByHour,
            resultTopProductsStats, resultAverageSalesByHour,
            resultAverageSalesByDayOfWeek, resultSellerPerformance,
            resultOrganisationMontlyRevenue,
        ] = await Promise.all([
            dashboardService.getYearSalesByMonth(organisationId),
            dashboardService.getMonthSalesByWeek(organisationId),
            dashboardService.getWeekSalesByDay(organisationId),
            dashboardService.getTodaySalesByHour(organisationId),
            dashboardService.getTopProductsStats(organisationId),
            dashboardService.getAverageSalesByHour(organisationId),
            dashboardService.getAverageSalesByDayOfWeek(organisationId),
            dashboardService.getSellerPerformance(organisationId),
            dashboardService.getOrganisationMonthlyRevenueAndNextInvoiceDate(organisationId),
        ]);

        if(resultYearSalesByMonth.code === 200){
            setYearSalesByMonth(resultYearSalesByMonth.data);
        } else {
            setGetYearSalesByMonthError({
                message: resultYearSalesByMonth.code === 400 ?
                    "Impossible de récupérer les ventes de l'année par mois" :
                    "Une erreur est survenue lors de la récupération des ventes de l'année par mois",
            });
        }

        if(resultMonthSalesByWeek.code === 200){
            setMonthSalesByWeek(resultMonthSalesByWeek.data);
        } else {
            setGetMonthSalesByWeekError({
                message: resultMonthSalesByWeek.code === 400 ?
                    "Impossible de récupérer les ventes du mois par semaine" :
                    "Une erreur est survenue lors de la récupération des ventes du mois par semaine",
            });
        }

        if(resultWeekSalesByDay.code === 200){
            setWeekSalesByDay(resultWeekSalesByDay.data);
        } else {
            setGetWeekSalesByDayError({
                message: resultWeekSalesByDay.code === 400 ?
                    "Impossible de récupérer les ventes de la semaine par jour" :
                    "Une erreur est survenue lors de la récupération des ventes de la semaine par jour",
            });
        }

        if(resultTodaySalesByHour.code === 200){
            setTodaySalesByHour(resultTodaySalesByHour.data);
        } else {
            setGetTodaySalesByHourError({
                message: resultTodaySalesByHour.code === 400 ?
                    "Impossible de récupérer les ventes d'aujourd'hui par heure" :
                    "Une erreur est survenue lors de la récupération des ventes d'aujourd'hui par heure",
            });
        }

        if(resultTopProductsStats.code === 200){
            setTopProductsStats(resultTopProductsStats.data);
        } else {
            setGetTopProductsStatsError({
                message: resultTopProductsStats.code === 400 ?
                    "Impossible de récupérer les statistiques des produits" :
                    "Une erreur est survenue lors de la récupération des statistiques des produits",
            });
        }

        if(resultAverageSalesByHour.code === 200){
            setAverageSalesByHour(resultAverageSalesByHour.data);
        } else {
            setGetAverageSalesByHourError({
                message: resultAverageSalesByHour.code === 400 ?
                    "Impossible de récupérer la moyenne des ventes par heure" :
                    "Une erreur est survenue lors de la récupération de la moyenne des ventes par heure",
            });
        }

        if(resultAverageSalesByDayOfWeek.code === 200){
            setAverageSalesByDayOfWeek(resultAverageSalesByDayOfWeek.data);
        } else {
            setGetAverageSalesByDayOfWeekError({
                message: resultAverageSalesByDayOfWeek.code === 400 ?
                    "Impossible de récupérer la moyenne des ventes par jour de la semaine" :
                    "Une erreur est survenue lors de la récupération de la moyenne des ventes par jour de la semaine",
            });
        }

        if(resultSellerPerformance.code === 200){
            setSellerPerformance(resultSellerPerformance.data);
        } else {
            setGetSellerPerformanceError({
                message: resultSellerPerformance.code === 400 ?
                    "Impossible de récupérer la performance des vendeurs" :
                    "Une erreur est survenue lors de la récupération de la performance des vendeurs",
            });
        }

        if(resultOrganisationMontlyRevenue.code === 200){
            setOrganisationMontlyRevenue(resultOrganisationMontlyRevenue.data);
        } else {
            setGetOrganisationMontlyRevenueError({
                message: resultOrganisationMontlyRevenue.code === 400 ?
                    "Impossible de récupérer le revenu mensuel de l'organisation" :
                    "Une erreur est survenue lors de la récupération du revenu mensuel de l'organisation",
            });
        }

    }, [
        organisation, setOrganisationMontlyRevenue,
        setAverageSalesByDayOfWeek, setAverageSalesByHour, 
        setMonthSalesByWeek, setSellerPerformance, 
        setTodaySalesByHour, setTopProductsStats, 
        setWeekSalesByDay, setYearSalesByMonth,
    ]);

    return {
        yearSalesByMonth, monthSalesByWeek,
        weekSalesByDay, todaySalesByHour,
        topProductsStats, averageSalesByHour,
        averageSalesByDayOfWeek, sellerPerformance,
        getYearSalesByMonthError, getMonthSalesByWeekError,
        getWeekSalesByDayError, getTodaySalesByHourError,
        getTopProductsStatsError, getAverageSalesByHourError,
        getAverageSalesByDayOfWeekError, getSellerPerformanceError,
        organisationMontlyRevenue, getOrganisationMontlyRevenueError,
        fetchAllDashboardData,
    }
};
